import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import { Warehouse } from "src/types/warehouse.types";

interface AddWarehouseModalProps {
  warehouse: Warehouse | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AddWarehouseModal: React.FC<AddWarehouseModalProps> = ({
  warehouse,
  onClose,
  onSuccess,
}) => {
  const isEdit = !!warehouse?._id;
  const [name, setName] = useState(warehouse?.name || "");
  const [code, setCode] = useState(warehouse?.code || "");
  const [address, setAddress] = useState(warehouse?.address || "");
  const [touched, setTouched] = useState(false);
  const dispatch = useAppDispatch();
  const { createWarehouse, editWarehouse } = useAppSelector(
    (state) => state.warehouse
  );
  const loading = isEdit ? editWarehouse.loading : createWarehouse.loading;
  const { singular: warehouseLabel } = useWarehouseLabel();

  useEffect(() => {
    if (isEdit && editWarehouse.data) {
      toast.success(`${warehouseLabel} updated successfully`);
      dispatch(warehouseActions.resetEditWarehouse());
      onSuccess();
    } else if (isEdit && editWarehouse.error) {
      toast.error(`Error: ${editWarehouse.error}`);
      dispatch(warehouseActions.resetEditWarehouse());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editWarehouse]);

  useEffect(() => {
    if (!isEdit && createWarehouse.data) {
      toast.success(`${warehouseLabel} created successfully`);
      dispatch(warehouseActions.resetCreateWarehouse());
      onSuccess();
    } else if (!isEdit && createWarehouse.error) {
      toast.error(`Error: ${createWarehouse.error}`);
      dispatch(warehouseActions.resetCreateWarehouse());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createWarehouse]);

  const handleSubmit = () => {
    setTouched(true);
    if (!name.trim()) return;
    const payload = {
      name: name.trim(),
      code: code.trim() || undefined,
      address: address.trim() || undefined,
    };
    if (isEdit) {
      dispatch(warehouseActions.editWarehouseRequest({ _id: warehouse!._id, ...payload }));
    } else {
      dispatch(warehouseActions.createWarehouseRequest(payload));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit ${warehouseLabel.toLowerCase()}` : `Add ${warehouseLabel.toLowerCase()}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={loading} disabled={loading}>
            {isEdit ? "Save changes" : `Add ${warehouseLabel}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label={`${warehouseLabel} name`}
          placeholder="e.g. Bangalore-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          error={touched && !name.trim()}
          helperText={touched && !name.trim() ? `${warehouseLabel} name is required` : undefined}
        />
        <Input
          label="Code"
          optional
          placeholder="e.g. BLR1"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Input
          label="Address"
          optional
          placeholder="e.g. HSR Layout, Bengaluru"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
      </div>
    </Modal>
  );
};

export default AddWarehouseModal;
